import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { resetAttractionImage } from '~/services/AttractionsService';

import type { Attraction } from '~/types/attractionTypes';

import { attractionQueryKeys } from './attractionQueryKeys';

/**
 * Resets the hero image to auto-selected.
 * Uses optimistic update so the UI reflects instantly.
 * Marks the detail query stale (without immediate refetch) to avoid CDN cache reverting the update.
 */
export function useResetAttractionImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (attractionId: string) => {
      return await resetAttractionImage(attractionId);
    },
    onMutate: async (attractionId) => {
      await queryClient.cancelQueries({ queryKey: attractionQueryKeys.detail(attractionId) });

      const previousAttraction = queryClient.getQueryData<Attraction>(attractionQueryKeys.detail(attractionId));

      if (previousAttraction) {
        queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), {
          ...previousAttraction,
          imageUrl: null,
        });
      }

      return { previousAttraction };
    },
    onSuccess: (_data, attractionId) => {
      // Mark detail as stale without immediate refetch to avoid CDN cache
      // serving the pre-reset image and reverting the optimistic update.
      queryClient.invalidateQueries({
        queryKey: attractionQueryKeys.detail(attractionId),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      enqueueSnackbar('Hero image reset to auto', { variant: 'success' });
    },
    onError: (error: Error, attractionId, context) => {
      if (context?.previousAttraction) {
        queryClient.setQueryData(attractionQueryKeys.detail(attractionId), context.previousAttraction);
      }
      enqueueSnackbar(`Error resetting image: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
